import type { ActivatedRouteSnapshot } from '@angular/router';

/**
 * Parses a positive integer entity ID from a single route parameter token.
 * @returns The ID, or null when the token is missing, blank, non-integer or not positive.
 */
export function parsePositiveIdFromUrlToken(
    token: string | null
): number | null {
    if (token == null) {
        return null;
    }

    const id = Number(token);
    return Number.isInteger(id) && id > 0 ? id : null;
}

/**
 * Finds a positive integer entity ID carried by the `paramName` route parameter anywhere at or
 * below `root`, searching depth-first — the first match wins.
 *
 * @param root Route snapshot to search from (and its descendants). Pass
 * `router.routerState.snapshot.root` to search the whole active route tree — needed when the
 * param may live on an ancestor route (e.g. `caseId`, set on a parent route and read by many
 * descendants). Pass a specific route's own snapshot (e.g. `this.route.snapshot`) when the
 * param is known to live on that exact route — a leaf ID owned by a single component.
 * @param paramName Name of the route parameter holding the ID, e.g. `'caseId'`.
 * @returns The ID, or null when no route in the searched subtree carries a valid one.
 */
export function findPositiveIdRouteParam(
    root: ActivatedRouteSnapshot,
    paramName: string
): number | null {
    const id = parsePositiveIdFromUrlToken(root.paramMap.get(paramName));

    if (id != null) {
        return id;
    }

    for (const child of root.children) {
        const childId = findPositiveIdRouteParam(child, paramName);

        if (childId != null) {
            return childId;
        }
    }

    return null;
}

/**
 * Same lookup as `findPositiveIdRouteParam`, for callers that only run inside a route whose
 * `positiveIdParamGuard` already guaranteed the ID — so a missing one is a programming error
 * rather than a state to branch on.
 *
 * @param root Route snapshot to search from (and its descendants) — see
 * {@link findPositiveIdRouteParam} for when to pass the full tree root vs. a specific route's
 * own snapshot. Whichever is passed here must be the route (or route tree) actually covered by
 * the corresponding `positiveIdParamGuard`, or the "guaranteed" contract this throws on doesn't
 * hold.
 * @param paramName Name of the route parameter holding the ID, e.g. `'caseId'`.
 * @param missingMessage Message for the error thrown when no route carries a valid ID.
 * @returns The ID.
 * @throws Error when called outside a route carrying a valid `paramName`.
 */
export function requirePositiveIdRouteParam(
    root: ActivatedRouteSnapshot,
    paramName: string,
    missingMessage: string
): number {
    const id = findPositiveIdRouteParam(root, paramName);

    if (id == null) {
        throw new Error(missingMessage);
    }

    return id;
}
